/* import __COLOCATED_TEMPLATE__ from './generate-suggested-test-button.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent-2 */
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import { action } from '@ember/object';
import { tracked } from '@glimmer/tracking';
import { dropTask } from 'ember-concurrency-decorators';
import { type TaskGenerator } from 'ember-concurrency';
import { taskFor } from 'ember-concurrency-ts';
import { postRequest, ResponseError } from 'embercom/lib/inbox/requests';
import { camelizeKeys } from 'embercom/components/workflows/graph-editor/node-items/steps/fin-custom';
import type AppService from 'embercom/services/app-service';
import type RouterService from '@ember/routing/router-service';
import type ProcedurePreviewService from 'embercom/services/procedure-preview-service';
import type IntlService from 'embercom/services/intl';

interface Signature {
  Element: HTMLElement;
  Args: {};
}

interface ProcedureResponse {
  id: string;
  ruleset: {
    id: string;
    externalTaskStepId: string | null;
    workflowInstanceId: string;
  };
}

export default class GenerateSuggestedTestButton extends Component<Signature> {
  @service declare appService: AppService;
  @service declare router: RouterService;
  @service('procedure-preview-service') declare procedurePreviewService: ProcedurePreviewService;
  @service declare intl: IntlService;
  @service declare notificationsService: $TSFixMe;

  @tracked procedure: ProcedureResponse | null = null;
  @tracked isLoading = false;

  constructor(owner: unknown, args: Signature['Args']) {
    super(owner, args);
    taskFor(this.fetchProcedure).perform();
  }

  get procedureId(): string | undefined {
    // Navigate up the route hierarchy to find the procedure route params
    let route = this.router.currentRoute;
    while (route) {
      if (route.params && 'id' in route.params) {
        return route.params.id as string;
      }
      route = route.parent as typeof route;
    }
    return undefined;
  }

  get conversationId(): number | undefined {
    return this.procedurePreviewService.conversationId;
  }

  get isDisabled(): boolean {
    return !this.conversationId || this.isLoading || !this.procedure;
  }

  get externalTaskStepId(): string | null {
    return this.procedure?.ruleset?.externalTaskStepId ?? null;
  }

  get workflowId(): string | undefined {
    return this.procedure?.ruleset?.workflowInstanceId ?? this.procedureId;
  }

  @dropTask
  *fetchProcedure(): TaskGenerator<void> {
    let procedureId = this.procedureId;
    if (!procedureId) {
      return;
    }

    try {
      let response = yield fetch(
        `/ember/procedures/${procedureId}?app_id=${this.appService.app.id}`,
        {
          headers: {
            Accept: 'application/json',
            'Content-Type': 'application/json',
          },
        },
      );
      if (response.ok) {
        let rawProcedure = yield response.json();
        this.procedure = camelizeKeys(rawProcedure) as ProcedureResponse;
      }
    } catch (error) {
      // Silently fail - the button will remain disabled
      console.error('Failed to fetch procedure:', error);
    }
  }

  @action
  async generateSuggestedTest(): Promise<void> {
    if (this.isDisabled) {
      return;
    }

    this.isLoading = true;

    try {
      await postRequest(
        `/teammate_app/sophisticated_tests/generate_suggested_test_from_conversation?app_id=${this.appService.app.id}`,
        {
          workflow_id: this.workflowId,
          external_task_step_id: this.externalTaskStepId,
          conversation_id: this.conversationId,
        },
      );

      this.notificationsService.notifyConfirmation(
        this.intl.t('fin-ai-agent.train.procedures.generate-suggested-test.success'),
      );
    } catch (error) {
      if (error instanceof ResponseError) {
        this.notificationsService.notifyResponseError(error);
      } else {
        this.notificationsService.notifyError(
          this.intl.t('fin-ai-agent.train.procedures.generate-suggested-test.error'),
        );
      }
    } finally {
      this.isLoading = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'FinAiAgent::Procedures::GenerateSuggestedTestButton': typeof GenerateSuggestedTestButton;
    'fin-ai-agent/procedures/generate-suggested-test-button': typeof GenerateSuggestedTestButton;
  }
}
